import React from 'react';
import {
  ComposedChart,
  LineChart,
  BarChart,
  AreaChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  Legend,
  ResponsiveContainer,
  ReferenceLine, Tooltip,
} from 'recharts';
import DefaultTooltipContent from 'recharts/lib/component/DefaultTooltipContent';
import labeling from './labeling';

// Define type of visualisation - bar, line or area.
const defineType = (key, type, label, chartClick, options) => {
  const componentMap = {
    line: Line,
    bar: Bar,
    area: Area,
    refLine: Line,
    dashedLine: Line,
  };

  const { // Chart element modifiers.
    colour, stackId, dots,
  } = options;

  const TagName = componentMap[type];

  return (
    <TagName
      key={key}
      dataKey={key}
      name={label}
      fill={colour}
      legendType="line"
      isAnimationActive={false}

      {
        ...((type === 'line') && { strokeWidth: 2 })
      }

      {// Clicking is only supported in bar charts in cartesian series.
        ...(((type === 'bar')) && { onClick: chartClick })
      }

      {// For creating stackable bar
        ...(((type === 'bar') && stackId) && { stackId })
      }

      {// Both line and area have stroke colour and line's rigid type.
        ...((type !== 'bar') && {
          stroke: colour,
          type: 'monotone',
        })
      }

      {// If is a reference line or a dashed line, make sure it is in dashes.
        ...((type === 'refLine' || type === 'dashedLine') && { strokeDasharray: '3 3' })
      }

      dot={(type === 'refLine') ? false : dots}
    />
  );
};

const toolTipAddTotal = (props) => {
  const { payload } = props;
  if (!payload || payload.length === 0) return null;
  const payloadData = payload[0].payload;
  const totalValue = Object.keys(payloadData).reduce((a, c) => {
    const val = payloadData[c];
    const config = payload.find(item => item.dataKey === c);
    return (
      config
      && !config.strokeDasharray // don't add maximum ref-line related value.
      && (typeof val === 'number')
    ) ? (a + val) : a;
  }, 0);
  const sortedPayload = payload.sort((a, b) => b.name.localeCompare(a.name));
  const newPayload = [{ name: 'Total', value: +totalValue.toFixed(2) }, ...sortedPayload];

  // we render the default, but with our overridden payload
  return <DefaultTooltipContent {...props} payload={newPayload} />;
};

// For bar/line/area charts or composition of these.
const CartesianSeries = ({
  width,
  height,
  data,
  xRefLine,
  yRefLine,
  labelMap,
  labelSetForTicks,
  labelSetForData,
  elementMap,
  visType,
  color,
  chartClick,
  interval,
  legendItems = [],
}) => {
  const componentMap = {
    compose: ComposedChart,
    line: LineChart,
    bar: BarChart,
    area: AreaChart,
  };

  const ChartTagName = (visType) ? componentMap[visType] : componentMap.compose;

  return (
    <ResponsiveContainer
      width={width || '99%'}
      height={height || '99%'}
      {...(chartClick && { className: 'DCRPChartClickable' })}
    >
      <ChartTagName data={data}>
        <XAxis
          dataKey="name"
          tickFormatter={tick => labeling(tick, labelSetForTicks, labelMap)}
          {...(interval && { interval })}
        />
        <YAxis />
        <Legend payload={legendItems.sort((a, b) => a.value.localeCompare(b.value))} />
        <Tooltip
          labelFormatter={label => labeling(label, labelSetForData, labelMap)}
          content={props => toolTipAddTotal(props)}
        />
        { // This determines the elements added into the chart - is it a bar, a line or an area.
          Object.keys(data[0]).sort().map(key => (key !== 'name')
            && defineType(
              key,
              elementMap[key].type || visType || 'bar',
              labeling(key, labelSetForData, labelMap),
              chartClick,
              {
                colour: elementMap[key].color || color || '#900',
                stackId: elementMap[key].stackId || null,
                dots: (elementMap[key].dots === undefined) ? true : elementMap[key].dots,
              },
            ))
        }
        { // The horizontal line added for whatever purpose - e.g. maximum capacity for each day.
          xRefLine && (
            <ReferenceLine
              x={xRefLine.position}
              stroke={xRefLine.color}
              strokeDasharray={xRefLine.dash}
              label={xRefLine.label}
              isFront
            />
          )
        }
        { // The vertical line added for whatever purpose - e.g. which day is today.
          yRefLine && (
            <ReferenceLine
              y={yRefLine.position}
              stroke={yRefLine.color}
              strokeDasharray={yRefLine.dash}
              label={yRefLine.label}
              isFront
            />
          )
        }
      </ChartTagName>
    </ResponsiveContainer>
  );
};

export default CartesianSeries;
